import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

class VideoProcessor {
  constructor() {
    this.ffmpeg = new FFmpeg();
    this.isLoaded = false;
    this.isProcessing = false;
    this.currentProgress = 0;
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
  }

  async load() {
    if (this.isLoaded) return;
    
    try {
      // Load FFmpeg with the core files
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';
      await this.ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'text/wasm'),
      });
      this.isLoaded = true;
      console.log('FFmpeg loaded successfully');
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
      throw new Error('Failed to load video processing library');
    }
  }

  async transcodeVideo(inputFile, options = {}) {
    if (this.isProcessing) {
      throw new Error('Video processing is already in progress');
    }

    if (!this.isLoaded) {
      await this.load();
    }

    this.isProcessing = true;
    this.currentProgress = 0;

    try {
      // Set up progress monitoring
      this.ffmpeg.on('progress', ({ progress }) => {
        this.currentProgress = Math.round(progress * 100);
        if (this.onProgress) {
          this.onProgress(this.currentProgress);
        }
      });

      // Write input file to FFmpeg filesystem
      const inputFileName = 'input.mp4';
      const outputFileName = 'output.mp4';
      
      await this.ffmpeg.writeFile(inputFileName, await fetchFile(inputFile));

      // FFmpeg's log, to show why a conversion failed
      const log = [];
      const onLog = ({ message }) => { log.push(message); if (log.length > 30) log.shift(); };
      this.ffmpeg.on('log', onLog);

      // Build FFmpeg command
      const bitrate = options.bitrate || '10M'; // 10 Mbps default
      const command = [
        '-i', inputFileName,
        '-c:v', 'libx264',
        '-b:v', bitrate,
        '-c:a', 'aac',
        '-b:a', '128k',
        // FFmpeg sizes the video itself: the smaller side becomes at most 1080px, the other keeps the aspect ratio
        // and stays even. The browser can't be asked for the size - Chrome reports 0x0 for HEVC (iPhone) videos.
        // yuv420p turns 10-bit/HDR video into the 8-bit H.264 every browser and phone can play.
        '-vf', "scale='if(gt(iw,ih),-2,min(1080,iw))':'if(gt(iw,ih),min(1080,ih),-2)':flags=fast_bilinear,format=yuv420p",
        '-preset', 'veryfast',
        '-crf', '23',
        '-movflags', '+faststart',
        '-t', '30', // Cut video to 30 seconds maximum
        '-y', // Overwrite output file
        outputFileName
      ];

      console.log('Transcoding video with command:', command.join(' '));
      const info = await this.getVideoInfo(inputFile).catch(() => null);
      if (info) console.log(`Original (as the browser sees it): ${info.width}x${info.height}, ${Math.round(info.duration)}s`);

      // Execute FFmpeg command - it doesn't throw on failure, it returns a non-zero exit code
      const exitCode = await this.ffmpeg.exec(command);
      this.ffmpeg.off('log', onLog);
      const data = exitCode === 0 ? await this.ffmpeg.readFile(outputFileName).catch(() => null) : null;
      if (!data || data.length < 1024) {
        console.error(`FFmpeg failed (exit code ${exitCode}):\n${log.join('\n')}`);
        await this.ffmpeg.deleteFile(inputFileName).catch(() => {});
        await this.ffmpeg.deleteFile(outputFileName).catch(() => {});
        throw new Error('Video conversion failed - the video format may not be supported');
      }
      
      // Clean up files
      await this.ffmpeg.deleteFile(inputFileName);
      await this.ffmpeg.deleteFile(outputFileName);

      // Convert to Blob
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      
      if (this.onComplete) {
        this.onComplete(blob);
      }

      return blob;

    } catch (error) {
      console.error('Video transcoding failed:', error);
      if (this.onError) {
        this.onError(error);
      }
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  async getVideoInfo(file) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration
        });
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        reject(new Error('Failed to load video metadata'));
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  setCompleteCallback(callback) {
    this.onComplete = callback;
  }

  setErrorCallback(callback) {
    this.onError = callback;
  }

  getProgress() {
    return this.currentProgress;
  }

  isCurrentlyProcessing() {
    return this.isProcessing;
  }

  async cancel() {
    if (this.isProcessing) {
      try {
        await this.ffmpeg.terminate();
        this.isProcessing = false;
        this.currentProgress = 0;
        console.log('Video processing cancelled');
      } catch (error) {
        console.error('Error cancelling video processing:', error);
      }
    }
  }

  async reset() {
    try {
      // Terminate if currently processing
      if (this.isProcessing) {
        await this.ffmpeg.terminate();
      }
      
      // Reset all state
      this.isLoaded = false;
      this.isProcessing = false;
      this.currentProgress = 0;
      this.onProgress = null;
      this.onComplete = null;
      this.onError = null;
      
      // Create a new FFmpeg instance
      this.ffmpeg = new FFmpeg();
      
      console.log('Video processor reset successfully');
    } catch (error) {
      console.error('Error resetting video processor:', error);
    }
  }
}

// Create a singleton instance
const videoProcessor = new VideoProcessor();

export default videoProcessor;
